import React, { useEffect, useState } from 'react';
import { Plus, Eye, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import DataTable from '../../components/DataTable';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import ConfirmModal from '../../components/ConfirmModal';
import { bankService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

export default function BankAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const txForm = useForm();

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await bankService.getAll({ page, limit: 20 });
      setAccounts(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch { toast.error('Failed to load bank accounts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAccounts(); }, [page]);

  const openTx = async (account) => {
    setSelectedAccount(account);
    setTxModalOpen(true);
    try {
      const res = await bankService.getTransactions(account._id);
      setTransactions(res.data.data || []);
    } catch {}
  };

  const onSaveAccount = async (data) => {
    setSaving(true);
    try {
      await bankService.create(data);
      toast.success('Bank account created');
      setModalOpen(false);
      reset();
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const onAddTransaction = async (data) => {
    setSaving(true);
    try {
      await bankService.addTransaction(selectedAccount._id, { ...data, amount: Number(data.amount) });
      toast.success('Transaction added');
      txForm.reset();
      const res = await bankService.getTransactions(selectedAccount._id);
      setTransactions(res.data.data || []);
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const columns = [
    { key: 'accountName', label: 'Account Name', sortable: true },
    { key: 'bankName', label: 'Bank' },
    { key: 'accountNumber', label: 'Account No.' },
    { key: 'currency', label: 'Currency' },
    { key: 'balance', label: 'Balance', render: (v) => <span className={v < 0 ? 'text-red-600 font-bold' : 'text-green-700 font-bold'}>{formatCurrency(v)}</span> },
    { key: 'isActive', label: 'Status', render: (v) => <Badge color={v ? 'green' : 'gray'}>{v ? 'Active' : 'Inactive'}</Badge> },
  ];

  return (
    <div>
      <Breadcrumb />
      <PageHeader
        title="Bank Accounts"
        subtitle="Manage bank accounts and transactions"
        actions={<Button onClick={() => { reset(); setModalOpen(true); }}><Plus size={16} /> Add Account</Button>}
      />

      <DataTable
        columns={columns} data={accounts} loading={loading}
        totalItems={total} page={page} pageSize={20} onPageChange={setPage}
        title="Bank Accounts" exportFilename="bank_accounts"
        actions={(row) => (
          <>
            <button onClick={() => openTx(row)} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="View Transactions">
              <Eye size={15} />
            </button>
            <button onClick={() => setDeleteTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete">
              <span className="text-xs">✕</span>
            </button>
          </>
        )}
      />

      {/* Add Account Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Add Bank Account" size="lg">
        <form onSubmit={handleSubmit(onSaveAccount)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Account Name" required error={errors.accountName?.message}>
              <Input {...register('accountName', { required: 'Required' })} placeholder="e.g. Main Cash Account" />
            </FormField>
            <FormField label="Bank Name" required error={errors.bankName?.message}>
              <Input {...register('bankName', { required: 'Required' })} placeholder="Bank name" />
            </FormField>
            <FormField label="Account Number" required error={errors.accountNumber?.message}>
              <Input {...register('accountNumber', { required: 'Required' })} placeholder="Account number" />
            </FormField>
            <FormField label="Currency">
              <Select {...register('currency')}>
                <option value="AFN">AFN - Afghan Afghani</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="PKR">PKR - Pakistani Rupee</option>
                <option value="IRR">IRR - Iranian Rial</option>
              </Select>
            </FormField>
            <FormField label="Opening Balance">
              <Input type="number" step="0.01" {...register('balance')} placeholder="0" />
            </FormField>
            <FormField label="Notes">
              <Input {...register('notes')} placeholder="Notes" />
            </FormField>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>Save</Button>
          </div>
        </form>
      </Modal>

      {/* Transactions Modal */}
      <Modal isOpen={txModalOpen} onClose={() => setTxModalOpen(false)} title={`Transactions — ${selectedAccount?.accountName}`} size="xl">
        <div className="space-y-4">
          {/* Summary */}
          {selectedAccount && (
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
              <div>
                <p className="text-xs text-gray-500">Current Balance</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedAccount.balance)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Bank</p>
                <p className="text-sm font-medium text-gray-700">{selectedAccount.bankName}</p>
                <p className="text-xs text-gray-400">{selectedAccount.accountNumber}</p>
              </div>
            </div>
          )}

          {/* Add Transaction */}
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Add Transaction</p>
            <form onSubmit={txForm.handleSubmit(onAddTransaction)}>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Type">
                  <Select {...txForm.register('type', { required: true })}>
                    <option value="deposit">Deposit</option>
                    <option value="withdrawal">Withdrawal</option>
                    <option value="transfer">Transfer</option>
                  </Select>
                </FormField>
                <FormField label="Amount">
                  <Input type="number" step="0.01" {...txForm.register('amount', { required: true })} placeholder="0" />
                </FormField>
                <FormField label="Description">
                  <Input {...txForm.register('description')} placeholder="Description" />
                </FormField>
              </div>
              <div className="flex justify-end mt-3">
                <Button type="submit" size="sm" loading={saving}>Add Transaction</Button>
              </div>
            </form>
          </div>

          {/* Transactions list */}
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Type</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Description</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">Amount</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {transactions.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-sm">No transactions yet</td></tr>
                ) : transactions.map(tx => (
                  <tr key={tx._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1.5 text-xs font-medium ${tx.type === 'deposit' ? 'text-green-700' : 'text-red-700'}`}>
                        {tx.type === 'deposit' ? <ArrowUpCircle size={14} /> : <ArrowDownCircle size={14} />}
                        {tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{tx.description || '-'}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${tx.type === 'deposit' ? 'text-green-700' : 'text-red-700'}`}>
                      {tx.type === 'deposit' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-400 text-xs">{toJalali(tx.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await bankService.delete(deleteTarget._id);
            toast.success('Account deleted');
            fetchAccounts();
          } catch { toast.error('Failed'); }
          finally { setDeleting(false); setDeleteTarget(null); }
        }}
        loading={deleting}
        title="Delete Bank Account"
        message={`Delete "${deleteTarget?.accountName}"? All transactions will be removed.`}
      />
    </div>
  );
}
